import React from 'react'

interface InformationalPageProps {
  pageNum?: number
  headline?: string
  subheading?: string
  heading?: string
  message?: string
  image?: string
  contentByGender?: Record<string, { message?: string; summary?: string; image: string; metrics?: Record<string, any> }>
  benefits?: string[]
  trustBadges?: string[]
  testimonial?: { quote: string; author: string; role: string; image: string }
  cta?: string
  ctaText?: string
  onNext: () => void
  gender?: 'male' | 'female' | 'female_caregiver' | 'male_caregiver'
  isResultsPage?: boolean
}

export const InformationalPage: React.FC<InformationalPageProps> = ({
  headline,
  subheading,
  heading,
  message,
  image,
  contentByGender,
  benefits,
  trustBadges,
  testimonial,
  ctaText,
  onNext,
  gender = 'female',
  isResultsPage = false,
}) => {
  const content = contentByGender ? contentByGender[gender] : { message, image, summary: undefined }
  const displayMessage = message || content?.message || content?.summary || ''

  return (
    <div className="quiz-informational-page">
      <div className="quiz-container">
        {(headline || heading) && (
          <div className={`quiz-header ${isResultsPage ? 'results-header-section' : ''}`}>
            {isResultsPage && <div className="results-check">✓</div>}
            <h2>{headline || heading}</h2>
            {subheading && <p className="subheading">{subheading}</p>}
          </div>
        )}

        {image && (
          <div className="image-placeholder">
            <img src={image} alt={heading || headline} style={{ width: '100%', maxWidth: '600px', borderRadius: '8px' }} />
          </div>
        )}

        {content?.image && !image && (
          <div className="image-placeholder">
            <img src={content.image} alt={heading || headline} style={{ width: '100%', maxWidth: '600px', borderRadius: '8px' }} />
          </div>
        )}

        {displayMessage && (
          <div className="content-text">
            {(() => {
              const text = displayMessage || ''
              // Check if content has timeline or stats markup
              if (text.includes('<div class="timeline-item"') || text.includes('<div class="stat-item"')) {
                // Render HTML-like content safely by parsing it
                const renderStyledContent = (html: string) => {
                  const elements: React.ReactNode[] = []

                  // Match timeline items
                  let match
                  const timelineRegex = /<div class="timeline-item">\s*<span class="timeline-label">([^<]+)<\/span>\s*<span class="timeline-content">([^<]+)<\/span>\s*<\/div>/g
                  while ((match = timelineRegex.exec(html)) !== null) {
                    elements.push(
                      <div key={`timeline-${match.index}`} className="timeline-item">
                        <span className="timeline-label">{match[1]}</span>
                        <span className="timeline-content">{match[2]}</span>
                      </div>
                    )
                  }

                  // Match stats container
                  const statsMatch = html.match(/<div class="stats-container">(.*?)<\/div>/s)
                  if (statsMatch) {
                    const statsContent = statsMatch[1]
                    const headerMatch = statsContent.match(/<div class="stats-header">([^<]+)<\/div>/)
                    const statItems = statsContent.match(/<div class="stat-item">.*?<\/div>/g) || []

                    elements.push(
                      <div key="stats" className="stats-container">
                        {headerMatch && <div className="stats-header">{headerMatch[1]}</div>}
                        {statItems.map((item, idx) => {
                          const textMatch = item.match(/<span class="stat-text">([^<]+)<\/span>/)
                          return (
                            <div key={`stat-${idx}`} className="stat-item">
                              <span className="stat-check">✓</span>
                              <span className="stat-text" dangerouslySetInnerHTML={{ __html: textMatch?.[1] || '' }} />
                            </div>
                          )
                        })}
                      </div>
                    )
                  }

                  return elements
                }
                return renderStyledContent(text)
              } else {
                // Regular text with markdown-style bold and italic
                return text.split('\n').map((line: string, idx: number) => {
                  if (!line.trim()) return <div key={idx} style={{ height: '0.5rem' }} />

                  return (
                    <p key={idx} style={{ marginBottom: '1rem' }}>
                      {line.split(/(\*\*.*?\*\*|\*.*?\*)/g).map((part: string, i: number) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={i} style={{ color: '#1a1a1a', fontWeight: 700 }}>{part.slice(2, -2)}</strong>
                        }
                        if (part.startsWith('*') && part.endsWith('*')) {
                          return <em key={i}>{part.slice(1, -1)}</em>
                        }
                        return <span key={i}>{part}</span>
                      })}
                    </p>
                  )
                })
              }
            })()}
          </div>
        )}

        {benefits && benefits.length > 0 && (
          <div className="benefits-list">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="benefit-item">
                {benefit}
              </div>
            ))}
          </div>
        )}

        {trustBadges && trustBadges.length > 0 && (
          <div className="trust-badges">
            {trustBadges.map((badge, idx) => (
              <div key={idx} className="badge">
                ✓ {badge}
              </div>
            ))}
          </div>
        )}

        {testimonial && (
          <div className="testimonial">
            <div className="testimonial-image">
              <img src={testimonial.image} alt={testimonial.author} style={{ width: '100%', borderRadius: '8px' }} />
            </div>
            <div className="testimonial-content">
              <p className="quote">"{testimonial.quote}"</p>
              <p className="author">{testimonial.author}</p>
              <p className="role">{testimonial.role}</p>
            </div>
          </div>
        )}

        <div className="button-group">
          <button className="btn-primary" onClick={onNext}>
            {ctaText || 'Continue'}
          </button>
        </div>
      </div>
    </div>
  )
}
